import * as React from 'react'
import { Bot, MessageSquare } from 'lucide-react'

import { cn } from '../../lib/utils'

export type IngestChatMode = 'ask' | 'agent'

/** Ingest surfaces open on the agent run. Ask is a switch, not the landing. */
export const DEFAULT_INGEST_CHAT_MODE: IngestChatMode = 'agent'

const MODE_HINT: Record<IngestChatMode, string> = {
  ask: 'Questions about this ingest — separate from the agent run.',
  agent: 'Agent chain-of-thought, questions, and approvals.'
}

function ModeButton({
  mode,
  selected,
  onChange,
  compact,
  children
}: {
  mode: IngestChatMode
  selected: boolean
  onChange: (mode: IngestChatMode) => void
  compact: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      title={MODE_HINT[mode]}
      className={
        compact
          ? cn(
              'inline-flex items-center gap-1.5 px-1.5 py-0.5 text-xs transition-colors',
              selected
                ? 'font-medium text-foreground'
                : 'font-normal text-muted-foreground hover:text-foreground'
            )
          : cn(
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
              selected
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )
      }
      onClick={() => onChange(mode)}
    >
      {children}
    </button>
  )
}

/**
 * Segmented Ask / Agent switch for Current Ingest and conversation history.
 * Keeps the two lanes visually distinct so operators don't confuse Q&A with
 * the agent run thread.
 */
export function IngestChatModeTabs({
  value,
  onChange,
  agentBadgeCount = 0,
  ariaLabel,
  className,
  hint = true,
  size = 'default'
}: {
  value: IngestChatMode
  onChange: (mode: IngestChatMode) => void
  agentBadgeCount?: number
  ariaLabel: string
  className?: string
  /** Drop the one-line explainer where the switch shares a row with actions. */
  hint?: boolean
  /** `sm` is a text switch that sits in the page header like a title control. */
  size?: 'default' | 'sm'
}) {
  const compact = size === 'sm'

  return (
    <div
      className={cn(
        'flex shrink-0 flex-col',
        compact ? 'gap-0' : 'gap-1.5 border-b py-2',
        className
      )}
    >
      <div
        role="tablist"
        aria-label={ariaLabel}
        className={cn(
          'inline-flex w-fit max-w-full items-center',
          compact ? 'gap-0.5' : 'rounded-lg border border-border bg-muted/60 p-0.5'
        )}
      >
        <ModeButton mode="ask" selected={value === 'ask'} onChange={onChange} compact={compact}>
          <MessageSquare className="size-3.5 shrink-0" aria-hidden />
          Ask
        </ModeButton>
        {compact ? (
          <span className="px-1 text-xs text-muted-foreground/40" aria-hidden>
            |
          </span>
        ) : null}
        <ModeButton mode="agent" selected={value === 'agent'} onChange={onChange} compact={compact}>
          <Bot className="size-3.5 shrink-0" aria-hidden />
          Agent
          {agentBadgeCount > 0 ? (
            <span className="ml-1 inline-flex min-w-4 items-center justify-center rounded-full bg-st-warn-bg px-1 text-10 font-medium text-st-warn">
              {agentBadgeCount}
            </span>
          ) : null}
        </ModeButton>
      </div>
      {hint ? (
        <p className="m-0 text-11 leading-snug text-muted-foreground">{MODE_HINT[value]}</p>
      ) : null}
    </div>
  )
}
